// =====================================================================
// destinosManualConfig.js
// Una plantilla por hoja del Excel Cotizador_VV170326.xlsm.
// Cada plantilla indica su tipo de formulario, la función de cálculo
// (nombre exportado en calculos.js) y los textos del segundo mensaje.
// =====================================================================

import { textosIncluye } from "./textosIncluye"

const configs = {
  // ------------------------- TC INTERNACIONAL -------------------------
  internacional: {
    label: "TC Internacional",
    tipo: "estandar",
    calculo: "tcInternacional",
    // Código de destino que se manda al backend para elegir el template del
    // PDF (ver core/adaptarCotizacionManual.js). Los que no tienen template
    // propio dedicado usan "MANUAL": ese código no coincide con ninguno de
    // los recargos hardcodeados de templates/Prueba.html, así que cae
    // siempre en la fórmula genérica (no arrastra recargos de otro destino).
    destino: "MANUAL",
    usaTrm: true,
    usaTraslados: true,
    grupos: [
      {
        key: "ti",
        label: "Hoteles Todo Incluido",
        plan: "todo incluido",
        texto: textosIncluye.INTERNACIONAL_TI
      },
      {
        key: "desayuno",
        label: "Hoteles Sólo Desayuno",
        plan: "solo desayuno",
        texto: textosIncluye.INTERNACIONAL_DESAYUNO
      }
    ]
  },

  // --------------------------- TC NACIONAL ----------------------------
  nacional: {
    label: "TC Nacional",
    tipo: "estandar",
    calculo: "tcNacional",
    destino: "MANUAL",
    usaTrm: false,
    usaTraslados: false,
    notas: [
      "Si cotizas Amazonas debes sumar a los vuelos 80.000 (todo incluido) o 60.000 (solo desayuno) por el traslado."
    ],
    grupos: [
      {
        key: "ti",
        label: "Hoteles Todo Incluido",
        plan: "todo incluido",
        texto: textosIncluye.NACIONAL_TI
      },
      {
        key: "desayuno",
        label: "Hoteles Sólo Desayuno",
        plan: "solo desayuno",
        texto: textosIncluye.NACIONAL_DESAYUNO
      }
    ]
  },

  // ----------------------------- TC MEX -------------------------------
  mex: {
    label: "TC México (CDMX)",
    tipo: "estandar",
    calculo: "tcMex",
    destino: "MEX",
    usaTrm: true,
    usaTraslados: false,
    destinoFijo: "Ciudad de México",
    notas: ["Incluye tours en Ciudad de México según cantidad de personas."],
    grupos: [
      {
        key: "desayuno",
        label: "Hoteles Sólo Desayuno",
        plan: "solo desayuno",
        texto: textosIncluye.MEX
      }
    ]
  },

  // --------------------------- TC MEX-CUN -----------------------------
  mexCun: {
    label: "TC México + Cancún",
    tipo: "combinado",
    calculo: "tcMexCun",
    destino: "MEX COMBINADO",
    // Noches por ciudad — el template dedicado (COMBINADOMEX.html) las
    // necesita para el encabezado ("X NOCHES CDMX + Y NOCHES CANCÚN").
    requiereDiasCombi: true,
    // [diasCombi1, diasCombi2] — el backend toma diasCombi1 como el grupo
    // "ciudad" (grupoA) y diasCombi2 como el de playa (grupoB).
    labelsDiasCombi: ["Noches Ciudad de México", "Noches Cancún"],
    requiereTramoIntermedio: true,
    usaTrm: true,
    usaTraslados: false,
    destinoFijo: "Ciudad de México y Cancún",
    texto: textosIncluye.MEX_CUN,
    grupoA: { label: "Hoteles Ciudad de México (sólo desayuno)" },
    grupoB: [
      {
        key: "cunTi",
        label: "Hoteles Cancún Todo Incluido",
        plan: "todo incluido"
      }
    ]
  },

  // ----------------------------- TC CUN -------------------------------
  cancun: {
    label: "TC Cancún",
    tipo: "estandar",
    calculo: "tcCancun",
    destino: "CUN",
    usaTrm: true,
    usaTraslados: false,
    destinoFijo: "Cancún",
    // Mismo PDF que el automático (destinos/cun.js): hotel base + 2
    // adicionales con desayuno y hasta 10 todo incluido.
    grupos: [
      {
        key: "desayuno",
        label: "Hoteles Sólo Desayuno (hotel base + adicionales)",
        plan: "solo desayuno",
        texto: textosIncluye.CUN
      },
      {
        key: "ti",
        label: "Hoteles Todo Incluido",
        plan: "todo incluido",
        texto: textosIncluye.CUN
      }
    ]
  },

  playaDelCarmen: {
    label: "TC Playa del Carmen",
    tipo: "estandar",
    calculo: "tcPlayaDelCarmen",
    destino: "PCM",
    usaTrm: true,
    usaTraslados: false,
    destinoFijo: "Playa del Carmen",
    // Mismo PDF que el automático (destinos/pcm.js): hotel base + 2
    // adicionales con desayuno y hasta 10 todo incluido.
    grupos: [
      {
        key: "desayuno",
        label: "Hoteles Sólo Desayuno (hotel base + adicionales)",
        plan: "solo desayuno",
        texto: textosIncluye.CUN
      },
      {
        key: "ti",
        label: "Hoteles Todo Incluido",
        plan: "todo incluido",
        texto: textosIncluye.CUN
      }
    ]
  },

  // ----------------------------- TC PUJ -------------------------------
  // PUJ.html tiene 2 tablas de hotel independientes: "desayuno" (hotel base
  // + hasta 2 adicionales) y "hoteles_todo_incluido" (lista aparte, sin
  // límite fijo) — por eso hacen falta 2 grupos, no 1.
  puntaCana: {
    label: "TC Punta Cana",
    tipo: "estandar",
    calculo: "tcPuntaCana",
    destino: "PUJ",
    usaTrm: true,
    usaTraslados: false,
    destinoFijo: "Punta Cana",
    grupos: [
      {
        key: "desayuno",
        label: "Hoteles Con Desayuno (hotel base + adicionales)",
        plan: "solo desayuno",
        texto: textosIncluye.PUJ
      },
      {
        key: "ti",
        label: "Hoteles Todo Incluido",
        plan: "todo incluido",
        texto: textosIncluye.PUJ
      }
    ]
  },

  // ---------------------------- TC CUBA -------------------------------
  varadero: {
    label: "TC Cuba – Varadero",
    tipo: "estandar",
    calculo: "tcVaradero",
    destino: "VRA",
    usaTrm: true,
    usaTraslados: false,
    destinoFijo: "Varadero",
    notas: ["No incluye el pago de la visa electrónica a Cuba."],
    grupos: [
      {
        key: "ti",
        label: "Hoteles Todo Incluido",
        plan: "todo incluido",
        texto: textosIncluye.VARADERO
      }
    ]
  },

  habana: {
    label: "TC Cuba – Habana",
    tipo: "estandar",
    calculo: "tcHabana",
    destino: "HAV",
    usaTrm: true,
    usaTraslados: false,
    destinoFijo: "Habana",
    notas: ["No incluye el pago de la visa electrónica a Cuba."],
    grupos: [
      {
        key: "desayuno",
        label: "Hoteles Sólo Desayuno",
        plan: "solo desayuno",
        texto: textosIncluye.HABANA
      }
    ]
  },

  varaderoHabana: {
    label: "TC Cuba – Varadero + Habana",
    tipo: "combinado",
    calculo: "tcVaraderoHabana",
    destino: "HAV COMBINADO",
    requiereDiasCombi: true,
    labelsDiasCombi: ["Noches Habana", "Noches Varadero"],
    usaTrm: true,
    usaTraslados: false,
    destinoFijo: "Varadero y Habana",
    texto: textosIncluye.VARADERO_HABANA,
    notas: ["No incluye el pago de la visa electrónica a Cuba."],
    grupoA: { label: "Hoteles Habana (sólo desayuno)" },
    grupoB: [
      {
        key: "varTi",
        label: "Hoteles Varadero Todo Incluido",
        plan: "todo incluido"
      }
    ]
  },

  // --------------------------- TC PANAMA ------------------------------
  panamaPlaya: {
    label: "TC Panamá – Playa",
    tipo: "estandar",
    calculo: "tcPanamaPlaya",
    destino: "PTY",
    usaTrm: true,
    usaTraslados: false,
    destinoFijo: "Panamá",
    notas: [
      "Recargo nocturno de traslados (6PM-6AM): 20 USD por trayecto por persona. Se agrega manualmente."
    ],
    grupos: [
      {
        key: "ti",
        label: "Hoteles Todo Incluido",
        plan: "todo incluido",
        texto: textosIncluye.PANAMA_PLAYA
      }
    ]
  },

  panamaCiudad: {
    label: "TC Panamá – Ciudad",
    tipo: "estandar",
    calculo: "tcPanamaCiudad",
    destino: "PTYC",
    usaTrm: true,
    usaTraslados: false,
    destinoFijo: "Ciudad de Panamá",
    notas: [
      "Recargo nocturno de traslados (6PM-6AM): 20 USD por trayecto por persona. Se agrega manualmente."
    ],
    grupos: [
      {
        key: "desayuno",
        label: "Hoteles Sólo Desayuno",
        plan: "solo desayuno",
        texto: textosIncluye.PANAMA_CIUDAD
      }
    ]
  },

  panamaCombinado: {
    label: "TC Panamá – Playa + Ciudad",
    tipo: "combinado",
    calculo: "tcPanamaCombinado",
    destino: "PTY COMBINADO",
    requiereDiasCombi: true,
    labelsDiasCombi: ["Noches Ciudad", "Noches Playa"],
    usaTrm: true,
    usaTraslados: false,
    destinoFijo: "Panamá",
    texto: textosIncluye.PANAMA_COMBINADO,
    notas: [
      "Recargo nocturno de traslados (6PM-6AM): 20 USD por trayecto por persona. Se agrega manualmente."
    ],
    grupoA: { label: "Hoteles Ciudad de Panamá (sólo desayuno)" },
    grupoB: [
      {
        key: "playaTi",
        label: "Hoteles Playa Todo Incluido",
        plan: "todo incluido"
      }
    ]
  },

  // ------------------------ OTROS DESTINOS USD ------------------------
  otrosUsd: {
    label: "Otros destinos (USD)",
    tipo: "otrosUsd",
    calculo: "tcOtrosUSD",
    destino: "MANUAL",
    // El hotel se digita en USD; el PDF (Prueba.html) solo maneja COP, así
    // que se convierte con la TRM del día antes de armar el template.
    monedaHoteles: "USD",
    usaTrm: true,
    usaTraslados: false,
    notas: ["El hotel se digita en USD (neto Travel Compositor). El resultado se entrega en USD."],
    grupos: [
      {
        key: "hoteles",
        label: "Hoteles (valor total en USD)",
        plan: "",
        texto: textosIncluye.OTROS
      }
    ]
  },

  // --------------------------- SAN ANDRES -----------------------------
  sanAndres: {
    label: "San Andrés",
    tipo: "sanAndres",
    destino: "ADZ",
    usaTrm: false,
    usaTraslados: false,
    destinoFijo: "San Andrés",
    texto: textosIncluye.SAN_ANDRES
  },

  // ------------------------- COSTA (SMR / CTG) ------------------------
  // El template dedicado (SMR.html/CTG.html) muestra sus 3 categorías de
  // hotel SIEMPRE al mismo tiempo en el PDF, no una elegida con un selector
  // de plan — por eso "zonas" define 3 listas de hoteles simultáneas, cada
  // una con su propia tarifa por acomodación/noche.
  santaMarta: {
    label: "Santa Marta (temporada baja)",
    tipo: "costa",
    calculo: "costaConAlimentacion",
    destino: "SMR",
    usaTrm: false,
    usaTraslados: false,
    destinoFijo: "Santa Marta",
    texto: textosIncluye.COSTA,
    restriccion: "Tarifas de temporada baja",
    // En SMR.html las 3 categorías son planes de alimentación, no zonas.
    zonas: [
      { key: "desayuno", label: "Solo Desayuno" },
      { key: "completo", label: "Desayuno y Cena" },
      { key: "todo_incluido", label: "Todo Incluido" }
    ]
  },

  cartagena: {
    label: "Cartagena (temporada baja)",
    tipo: "costa",
    calculo: "costaConAlimentacion",
    destino: "CTG",
    usaTrm: false,
    usaTraslados: false,
    destinoFijo: "Cartagena",
    texto: textosIncluye.COSTA,
    restriccion: "Tarifas de temporada baja",
    // En CTG.html "desayuno"/"muralla" sí son zonas geográficas (Playa vs.
    // Ciudad Amurallada); Todo Incluido no distingue zona.
    zonas: [
      { key: "desayuno", label: "Playa (con desayuno)" },
      { key: "muralla", label: "Ciudad Amurallada (con desayuno)" },
      { key: "todo_incluido", label: "Todo Incluido" }
    ]
  },

  // -------------------------- GRUPOS TRAVEL ---------------------------
  gruposTravel: {
    label: "Grupos Travel",
    tipo: "gruposTravel",
    calculo: "gruposTravel",
    destino: "MANUAL",
    usaTrm: false,
    usaTraslados: false,
    grupos: [
      {
        key: "ti",
        label: "Hoteles Todo Incluido",
        plan: "todo incluido",
        texto: textosIncluye.GRUPOS_TRAVEL_TI
      },
      {
        key: "dc",
        label: "Hoteles Desayuno y Cena",
        plan: "desayuno y cena",
        texto: textosIncluye.GRUPOS_TRAVEL_DC
      },
      {
        key: "desayuno",
        label: "Hoteles Sólo Desayuno",
        plan: "solo desayuno",
        texto: textosIncluye.GRUPOS_TRAVEL_DESAYUNO
      }
    ]
  },

  // ------------------------------ TOURS -------------------------------
  turquia: {
    label: "Turquía",
    tipo: "tour",
    calculo: "turquia",
    // Paquete todo-en-uno (sin hoteles itemizados) — no hay template de PDF
    // compatible con este modelo de precio; sigue generando solo el mensaje
    // hasta que se diseñe un template propio.
    destino: null,
    pdfNoDisponible: true,
    usaTrm: true,
    saludo: textosIncluye.TURQUIA,
    incluye: textosIncluye.TURQUIA_INCLUYE,
    notas: [
      "El vuelo sale 1 día antes del inicio del plan y regresa 10 días después de la ida."
    ]
  },

  tailandia: {
    label: "Tailandia",
    // Tipo propio: a diferencia de Turquía, sí tiene template dedicado
    // (BKK12DIAS.html/BKK13DIAS.html), pero ese template necesita fechas de
    // check-in/checkout y habitaciones por ocupación (igual que el formulario
    // "estandar"/San Andrés), no el formulario de "tour" (inicioPlan/
    // nochesAdicionales). Por eso usa su propio tipo de formulario.
    tipo: "bkk",
    destino: "BKK",
    dias: 12, // 12 o 13, seleccionable en el formulario
    usaTrm: true,
    incluye: textosIncluye.TAILANDIA_INCLUYE,
    notas: [
      "Los 3 hoteles corresponden a las categorías Turista, Premium y 5 Estrellas.",
      "Las tarifas de circuito (Selección/Clásico/Premium) se consultan en vivo contra Special Tours al generar el PDF; si no hay salida disponible para la fecha, ese valor queda en $0 y debe ajustarse manualmente."
    ]
  },

  // ----------------------------- CRUCERO ------------------------------
  cruceroCtg: {
    label: "Crucero desde Cartagena",
    tipo: "crucero",
    calculo: "cruceroCtg",
    // Precio por cabina, no por hotel — mismo caso que Turquía: no hay
    // template de PDF compatible con este modelo hoy.
    destino: null,
    pdfNoDisponible: true,
    usaTrm: false,
    texto: textosIncluye.CRUCERO,
    notas: [
      "La naviera se asigna automáticamente según la fecha de salida (Serenade / Grandeur of the Seas)."
    ]
  }
}

export const destinosManual = Object.entries(configs).map(([value, cfg]) => ({
  value,
  label: cfg.label
}))

// CTG/SMR tienen categorías de hotel propias (zonas: playa/muralla/TI en
// Cartagena, desayuno/completo/TI en Santa Marta) distintas de la lista
// simple ti/desayuno del resto de plantillas. Si el destino efectivo de
// CUALQUIER plantilla (no solo "Cartagena"/"Santa Marta" del desplegable)
// resuelve a CTG o SMR — ver resolverDestinoLibre — el formulario de
// hoteles debe mostrar esas zonas en vez de la lista genérica.
const ZONAS_POR_DESTINO_COSTA = {
  CTG: configs.cartagena.zonas,
  SMR: configs.santaMarta.zonas
}

export function zonasParaDestino(codigoDestino) {
  return ZONAS_POR_DESTINO_COSTA[codigoDestino] || null
}

export function getDestinoManual(value) {
  return configs[value] ?? null
}

// TC Internacional / TC Nacional / Grupos Travel / Otros USD mandan
// destino: "MANUAL" (plantilla genérica, sin código fijo) y el usuario
// escribe el destino como texto libre. Pero templates/Prueba.html y
// pdf/general.js SÍ tienen recargos y nombres propios hardcodeados para
// varios códigos IATA (GIG, AUA, CUR, MBJ, SJD no tienen plantilla manual
// dedicada — son solo alcanzables vía "MANUAL" — y JFK/LAX/MAD/BCN/SFO/
// CDG/FCO/GRU solo afectan el nombre mostrado). Si lo que el usuario
// escribió coincide con uno de estos, hay que mandar el código real en vez
// de "MANUAL" para que tome el recargo/nombre correctos — igual que ya
// pasa cuando el destino automático produce esos mismos códigos.
// Nombre/código libre → código real de destino. Se usa SOLO cuando
// cfg.destino === "MANUAL" (TC Nacional/Internacional/Grupos Travel/Otros
// USD) — nunca reemplaza el código de una plantilla con destino fijo
// (Punta Cana, San Andrés, etc.) ni cambia la plantilla activa: el
// formulario se queda tal cual (lista simple hoteles.ti/hoteles.desayuno),
// solo cambia el código que se manda al backend para que el PDF salga con
// el template/recargo/nombre correctos. Los destinos con plantilla propia
// (CTG, SMR, ADZ, PUJ, MEX, CUN, PCM, VRA, HAV, PTY, PTYC) leen ese mismo
// hoteles.ti/hoteles.desayuno como entrada — ver core/adaptarCotizacionManual.js.
const ALIAS_DESTINO_LIBRE = {
  "BRASIL": "GIG",
  "RIO DE JANEIRO": "GIG",
  "RIO": "GIG",
  "GIG": "GIG",
  "ARUBA": "AUA",
  "ORANJESTAD": "AUA",
  "AUA": "AUA",
  "CURAZAO": "CUR",
  "CURACAO": "CUR",
  "CUR": "CUR",
  "JAMAICA": "MBJ",
  "MONTEGO BAY": "MBJ",
  "MBJ": "MBJ",
  "LOS CABOS": "SJD",
  "CABO SAN LUCAS": "SJD",
  "SJD": "SJD",
  "NUEVA YORK": "JFK",
  "NEW YORK": "JFK",
  "JFK": "JFK",
  "LOS ANGELES": "LAX",
  "LAX": "LAX",
  "MADRID": "MAD",
  "MAD": "MAD",
  "BARCELONA": "BCN",
  "BCN": "BCN",
  "SAN FRANCISCO": "SFO",
  "SFO": "SFO",
  "PARIS": "CDG",
  "CDG": "CDG",
  "ROMA": "FCO",
  "FCO": "FCO",
  "SAO PAULO": "GRU",
  "SAN PABLO": "GRU",
  "GRU": "GRU",
  "CARTAGENA": "CTG",
  "CARTAGENA DE INDIAS": "CTG",
  "CTG": "CTG",
  "SANTA MARTA": "SMR",
  "SMR": "SMR",
  "SAN ANDRES": "ADZ",
  "SAN ANDRES ISLAS": "ADZ",
  "PROVIDENCIA": "ADZ",
  "ADZ": "ADZ",
  "PUNTA CANA": "PUJ",
  "PUJ": "PUJ",
  "MEXICO": "MEX",
  "CIUDAD DE MEXICO": "MEX",
  "CDMX": "MEX",
  "MEX": "MEX",
  "CANCUN": "CUN",
  "CUN": "CUN",
  "PLAYA DEL CARMEN": "PCM",
  "PCM": "PCM",
  "VARADERO": "VRA",
  "VRA": "VRA",
  "HABANA": "HAV",
  "LA HABANA": "HAV",
  "HAV": "HAV",
  "PANAMA PLAYA": "PTY",
  "PLAYA PANAMA": "PTY",
  "PTY": "PTY",
  "PANAMA CIUDAD": "PTYC",
  "CIUDAD DE PANAMA": "PTYC",
  "PANAMA CITY": "PTYC",
  "PTYC": "PTYC"
}

function normalizarNombreDestino(texto) {
  return (texto || "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "") // quita tildes
    .trim().toUpperCase()
}

export function resolverDestinoLibre(nombreLibre) {
  return ALIAS_DESTINO_LIBRE[normalizarNombreDestino(nombreLibre)] || null
}
